import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useSubscription } from 'react-stomp-hooks';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';

import { getUserPermissions } from 'src/services/user.service';
import { fetchDepartments, fetchMyActiveTokens } from 'src/services/department.service';

import AccessGrantedModal from 'src/sections/ticketCreation/AccessGrantedModal';

export default function PermissionSyncListener() {
  const { t } = useTranslation(); // Initialize the translator
  const { id: userId, token, fetchUser, updatePermissions } = useAuth();
  const { showToast } = useToast();

  const [departments, setDepartments] = useState([]);
  const [approvedRequest, setApprovedRequest] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Load departments once on mount to resolve cover images in the modal
  useEffect(() => {
    if (userId) {
      fetchDepartments()
        .then((res) => setDepartments(res || []))
        .catch((err) =>
          console.error('Failed to load departments inside listener:', err)
        );
    }
  }, [userId]);

  useSubscription(`/queue/permissions-${userId}`, async (message) => {
    if (message.body === 'PERMISSIONS_UPDATED') {
      try {
        const res = await getUserPermissions(userId);

        if (fetchUser && token) {
          await fetchUser(token);
        }

        if (updatePermissions && typeof updatePermissions === 'function') {
          updatePermissions(res.permissions || []);
        }

        // Fetch their active tokens list to grab the newly approved request details
        const activeTokens = await fetchMyActiveTokens();
        if (activeTokens && activeTokens.length > 0) {
          // Sort to find the absolute newest approved request
          const newest = [...activeTokens].sort((a, b) => b.id - a.id)[0];

          // Calculate the exact moment this token was approved (36 hours before expiresOn)
          const approvedAt = new Date(
            new Date(newest.expiresOn).getTime() - 36 * 60 * 60 * 1000
          );
          const secondsSinceApproval = Math.abs(new Date() - approvedAt) / 1000;

          // Double Protection Guard: ONLY pop the modal if the approval happened in the last 15 seconds
          if (secondsSinceApproval < 15) {
            setApprovedRequest({
              id: newest.id,
              targetDepartmentId: newest.targetDepartmentId,
              targetDepartmentName:
                departments.find((d) => d.id === newest.targetDepartmentId)?.name ||
                'Department',
              token: newest.token,
              expiresOn: newest.expiresOn,
            });
            setOpenModal(true); // Pop the modal instantly in real-time
          }
        }

        showToast({
          message:
            t('successMessages.permissionsUpdated') ||
            'Your permissions and departments have been updated.',
          severity: 'info',
        });
      } catch (e) {
        console.error('Failed to sync permissions and departments in real-time:', e);
      }
    }
  });

  return (
    <>
      {approvedRequest && (
        <AccessGrantedModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          request={approvedRequest}
          departments={departments}
        />
      )}
    </>
  );
}
